(() => {
  const DEFAULT_TITLE = 'CAN OZAN - AGLAMA BEN AGLARIM';
  const DEFAULT_NOTE = 'Hayatıma giren en güzel\nve en anlamlı hediyesin.\nSeni çok seviyorum sevgilim...';
  const DEFAULT_CURRENT = '0:54';
  const DEFAULT_DURATION = '3:11';
  const MAX_TITLE_LENGTH = 42;

  const normalizeSpotifyUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (raw.startsWith('spotify:track:')) return raw;

    try {
      const url = new URL(raw);
      if (!url.hostname.includes('spotify.com')) return '';
      const match = url.pathname.match(/\/track\/([A-Za-z0-9]+)/);
      return match ? `spotify:track:${match[1]}` : '';
    } catch (error) {
      return '';
    }
  };

  const normalizeYouTubeUrl = (value) => {
    try {
      const url = new URL(String(value || '').trim());
      const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
      if (!['youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtu.be'].includes(hostname)) return '';
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return url.toString();
    } catch (error) {
      return '';
    }
  };

  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.round(Number(milliseconds || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const formatTrackTitle = (track) => {
    const artist = String(track.artistName || '').trim();
    const name = String(track.trackName || '').trim();
    return [artist, name].filter(Boolean).join(' - ').normalize('NFC');
  };

  const clampTitleValue = (value) => String(value || '').normalize('NFC').slice(0, MAX_TITLE_LENGTH);
  const toTitleNameLoose = (value) => String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/(^|[\s-])(\p{L})/gu, (match, separator, letter) => `${separator}${letter.toLocaleUpperCase('tr-TR')}`);
  const primaryTitleCharacterPattern = /[\p{L}\p{N}]/u;
  const compactSearchQuery = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ıİ]/g, 'i')
    .replace(/[şŞ]/g, 's')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[çÇ]/g, 'c')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 10)
    .join('+');
  const isLocalHost = () => ['127.0.0.1', 'localhost'].includes(window.location.hostname);
  const isShopifyAppProxyPath = (value) => String(value || '').startsWith('/apps/');
  const SPOTIFY_SERVICE_ROOT = 'https://renao-kargo-takip.onrender.com/apps/renao-spotify';

  const resolveSpotifyServiceUrl = (configuredUrl, localPath) => {
    if (isLocalHost() && isShopifyAppProxyPath(configuredUrl)) {
      return `http://127.0.0.1:8791${localPath}`;
    }

    if (isShopifyAppProxyPath(configuredUrl)) {
      return `${SPOTIFY_SERVICE_ROOT}${localPath}`;
    }

    return configuredUrl;
  };

  const renderTitlePreview = (element, value) => {
    if (!element) return;
    element.textContent = '';
    Array.from(value).forEach((character) => {
      const span = document.createElement('span');
      span.textContent = character;
      if (!primaryTitleCharacterPattern.test(character)) {
        span.className = 'renao-music-poster__title-alt';
      }
      element.append(span);
    });
  };

  const sanitizeTimeValue = (value) => {
    const raw = String(value || '').replace(/[^\d:]/g, '');

    if (raw.includes(':')) {
      const [minutes = '', ...rest] = raw.split(':');
      const cleanMinutes = minutes.replace(/\D/g, '').slice(0, 2);
      const cleanSeconds = rest.join('').replace(/\D/g, '').slice(0, 2);
      return cleanMinutes ? `${cleanMinutes}:${cleanSeconds}` : cleanSeconds;
    }

    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    if (digits.length === 3) return `${digits.slice(0, 1)}:${digits.slice(1)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const isCompleteTimeValue = (value) => /^\d{1,2}:\d{2}$/.test(String(value || ''));

  const decodeHtmlEntities = (value) => {
    if (typeof value !== 'string') return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const setImage = (image, fallback, wrap, src, isQr = false) => {
    if (!image || !fallback) return;
    wrap?.classList.toggle('is-qr', isQr);
    wrap?.closest('.renao-music-poster')?.classList.toggle('is-qr-code', isQr);

    if (!src) {
      image.hidden = true;
      image.removeAttribute('src');
      fallback.hidden = false;
      return;
    }

    image.src = src;
    image.hidden = false;
    fallback.hidden = true;
  };

  const initMusicPersonalizer = (root) => {
    const isNoteMode = root.dataset.renaoMusicMode === 'note';
    const isPetFriendMode = root.classList.contains('renao-music-personalizer--pet-friend');
    const photoInput = root.querySelector('[data-renao-music-photo-input]');
    const photoButton = root.querySelector('[data-renao-music-photo-button]');
    const photoAdjust = root.querySelector('[data-renao-music-photo-adjust]');
    const photoField = root.querySelector('.renao-music-field--photo');
    const preview = root.querySelector('[data-renao-music-preview]');
    const photoPreview = root.querySelector('[data-renao-music-photo-preview]');
    const photoPlaceholder = root.querySelector('[data-renao-music-photo-placeholder]');
    const photoPosition = root.querySelector('[data-renao-music-photo-position]');
    const providerInput = root.querySelector('[data-renao-music-provider]');
    const providerOptions = root.querySelectorAll('[data-renao-music-provider-option]');
    const urlInput = root.querySelector('[data-renao-music-url]');
    const linkField = root.querySelector('[data-renao-music-link-field]');
    const linkToggle = root.querySelector('[data-renao-music-link-toggle]');
    const titleInput = root.querySelector('[data-renao-music-title]');
    const songField = root.querySelector('.renao-music-field--search');
    const currentInput = root.querySelector('[data-renao-music-current]');
    const durationInput = root.querySelector('[data-renao-music-duration]');
    const status = root.querySelector('[data-renao-music-status]');
    const results = root.querySelector('[data-renao-music-results]');
    const titlePreview = root.querySelector('[data-renao-music-title-preview]');
    const currentPreview = root.querySelector('[data-renao-music-current-preview]');
    const durationPreview = root.querySelector('[data-renao-music-duration-preview]');
    const progressDot = root.querySelector('[data-renao-music-progress]');
    const codeImage = root.querySelector('[data-renao-music-code-image]');
    const codeFallback = root.querySelector('[data-renao-music-code-fallback]');
    const codeWrap = root.querySelector('[data-renao-music-code-wrap]');
    const codeProperty = root.querySelector('[data-renao-music-code-property]');
    const selectedTrackInput = root.querySelector('[data-renao-music-selected-track]');
    const noteInput = root.querySelector('[data-renao-music-note]');
    const noteCount = root.querySelector('[data-renao-music-note-count]');
    const notePreview = root.querySelector('[data-renao-music-note-preview]');
    const noteSerifPreview = root.querySelector('[data-renao-music-note-serif]');
    const petNameInput = root.querySelector('[data-renao-pet-name]');
    const petNamePreview = root.querySelector('[data-renao-pet-name-preview]');
    const petNameError = root.querySelector('[data-renao-pet-name-error]');
    const petNamePoster = root.querySelector('.renao-pet-poster');
    const petNameFontProperty = root.querySelector('[data-renao-pet-name-font-property]');
    const petNameFontOptions = root.querySelectorAll('[data-renao-pet-name-font-option]');
    const petNoteToggle = root.querySelector('[data-renao-pet-note-toggle]');
    const petDateInput = root.querySelector('[data-renao-pet-date]');
    const petDatePreview = root.querySelector('[data-renao-pet-date-preview]');
    const petDateToggle = root.querySelector('[data-renao-pet-date-toggle]');
    const signatureInput = root.querySelector('[data-renao-music-signature]');
    const signatureToggle = root.querySelector('[data-renao-music-signature-toggle]');
    const signaturePreview = root.querySelector('[data-renao-music-signature-preview]');
    const codeFallbackImage = codeFallback?.querySelector('img');
    const proofOpen = root.querySelector('[data-renao-music-proof-open]');
    const proofModal = root.querySelector('[data-renao-music-proof-modal]');
    const proofStage = root.querySelector('[data-renao-music-proof-stage]');
    const proofCloseButtons = root.querySelectorAll('[data-renao-music-proof-close]');
    const cropModal = root.querySelector('[data-renao-music-crop]');
    const cropStage = root.querySelector('[data-renao-music-crop-stage]');
    const cropImage = root.querySelector('[data-renao-music-crop-image]');
    const cropZoom = root.querySelector('[data-renao-music-crop-zoom]');
    const cropApply = root.querySelector('[data-renao-music-crop-apply]');
    const cropCancelButtons = root.querySelectorAll('[data-renao-music-crop-cancel]');
    const cropMoveButtons = root.querySelectorAll('[data-renao-music-crop-move]');
    const cropZoomStepButtons = root.querySelectorAll('[data-renao-music-crop-zoom-step]');
    const cropReset = root.querySelector('[data-renao-music-crop-reset]');
    const visualStyleInput = root.querySelector('[data-renao-visual-style-input]');
    const visualStyleOptions = root.querySelectorAll('[data-renao-visual-style-option]');
    const visualStyleNote = root.querySelector('[data-renao-visual-style-note]');
    const getSpotifySearchUrl = () => {
      const configuredUrl = root.dataset.renaoSpotifySearchUrl || window.renaoSpotifySearchUrl || '/apps/renao-spotify/search';

      return resolveSpotifyServiceUrl(configuredUrl, '/search');
    };

    const getSpotifyCodeUrl = (spotifyUri) => {
      const configuredUrl = root.dataset.renaoSpotifyCodeUrl || window.renaoSpotifyCodeUrl || '/apps/renao-spotify/code';
      const codeUrl = new URL(resolveSpotifyServiceUrl(configuredUrl, '/code'), window.location.origin);

      codeUrl.searchParams.set('uri', spotifyUri);
      codeUrl.searchParams.set('size', '1280');
      codeUrl.searchParams.set('v', Date.now().toString(36));
      return codeUrl.toString();
    };

    const getStyledQrUrl = (targetUrl) => {
      const configuredUrl = root.dataset.renaoQrCodeUrl || window.renaoQrCodeUrl || '/apps/renao-spotify/qr';
      const qrUrl = new URL(resolveSpotifyServiceUrl(configuredUrl, '/qr'), window.location.origin);

      qrUrl.searchParams.set('text', targetUrl);
      qrUrl.searchParams.set('size', '960');
      qrUrl.searchParams.set('style', 'brush-v3');
      return qrUrl.toString();
    };

    const getShortenUrl = () => {
      const configuredUrl = root.dataset.renaoShortenUrl || window.renaoShortenUrl || '/apps/renao-spotify/shorten';
      return resolveSpotifyServiceUrl(configuredUrl, '/shorten');
    };

    const getYouTubeMetadataUrl = (targetUrl) => {
      const metadataUrl = new URL('https://noembed.com/embed');
      metadataUrl.searchParams.set('url', targetUrl);
      return metadataUrl.toString();
    };

    const setCodeFallbackProvider = (provider) => {
      if (!codeFallbackImage) return;
      const placeholder = provider === 'YouTube'
        ? root.dataset.renaoQrPlaceholder
        : root.dataset.renaoSpotifyPlaceholder;
      if (placeholder && codeFallbackImage.getAttribute('src') !== placeholder) {
        codeFallbackImage.setAttribute('src', placeholder);
      }
    };

    if (!photoInput) return;
    if (!isNoteMode && (!providerInput || !urlInput || !titleInput)) return;
    if (cropModal && cropModal.parentElement !== document.body) document.body.appendChild(cropModal);
    if (cropImage) cropImage.draggable = false;

    let objectUrl = '';
    let searchRun = 0;
    let searchTimer = 0;
    let qrRun = 0;
    let youtubeMetadataRun = 0;
    let youtubeMetadataTimer = 0;
    let youtubeMetadataStatus = 'idle';
    let selectedSpotifyUri = '';
    let selectedTrackTitle = '';
    let isSongConfirmed = false;
    let isManualLinkConfirmed = false;
    const shortUrlCache = new Map();
    let cropState = { scale: 1, x: 0, y: 0 };
    let cropReferenceSize = { width: 0, height: 0 };
    let dragState = null;
    let existingPhotoUrl = '';
    const previewOriginalParent = preview?.parentElement || null;
    const previewOriginalNext = preview?.nextSibling || null;

    const setStatus = (message) => {
      if (status) status.textContent = message;
    };

    const getDisplayPhotoUrl = () => objectUrl || existingPhotoUrl;

    const setPhotoPreviewSource = (src) => {
      if (photoPreview) {
        if (src) {
          photoPreview.src = src;
        } else {
          photoPreview.removeAttribute('src');
        }
      }

      if (cropImage && !cropModal?.hidden && src) cropImage.src = src;
    };

    const getProductForm = () => root.closest('form');

    const setExistingPhotoProperty = (url) => {
      const productForm = getProductForm();
      if (!productForm) return;

      let input = productForm.querySelector('[data-renao-music-existing-photo-property]');
      if (!url) {
        input?.remove();
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Fotoğraf]';
        input.dataset.renaoMusicExistingPhotoProperty = '';
        productForm.appendChild(input);
      }

      input.value = url;
    };

    const setExistingPhoto = (url) => {
      existingPhotoUrl = String(url || '').trim();
      root.dataset.renaoExistingPhoto = existingPhotoUrl ? 'true' : 'false';
      setExistingPhotoProperty(existingPhotoUrl);

      if (existingPhotoUrl && photoPreview && !objectUrl) photoPreview.src = existingPhotoUrl;
      if (!existingPhotoUrl && !objectUrl && photoPreview) photoPreview.removeAttribute('src');
      if (photoPlaceholder) photoPlaceholder.hidden = Boolean(objectUrl || existingPhotoUrl);
      if (photoAdjust) photoAdjust.hidden = !objectUrl;
    };

    const getProductMediaWrapper = () => {
      const product = root.closest('.product') || document.querySelector('.product');
      return product ? product.querySelector('.product__media-wrapper') : null;
    };

    const showProductMedia = () => {
      const mediaWrapper = getProductMediaWrapper();
      if (mediaWrapper) {
        mediaWrapper.classList.remove('has-renao-live-preview');
        mediaWrapper.classList.remove('is-renao-media-returning');
        void mediaWrapper.offsetWidth;
        mediaWrapper.classList.add('is-renao-media-returning');
        window.setTimeout(() => mediaWrapper.classList.remove('is-renao-media-returning'), 900);
      }

      root.classList.remove('has-renao-live-preview');
      if (preview && previewOriginalParent && preview.parentElement !== previewOriginalParent) {
        previewOriginalParent.insertBefore(preview, previewOriginalNext);
      }
    };

    const ensureLivePreviewDock = (mediaWrapper) => {
      let dock = mediaWrapper.querySelector('[data-renao-music-live-preview-dock]');
      if (dock) return dock;

      dock = document.createElement('div');
      dock.className = 'renao-live-preview-dock renao-live-preview-dock--music';
      dock.dataset.renaoMusicLivePreviewDock = '';
      dock.innerHTML = `
        <div class="renao-live-preview-dock__bar">
          <span class="renao-live-preview-dock__title">
            <span class="renao-live-preview-dock__mark" aria-hidden="true"></span>
            <span>
              <strong>Canlı önizleme</strong>
              <small>Baskıdan önceki son tasarım</small>
            </span>
          </span>
          <button type="button" data-renao-music-show-product-media>Ürün fotoğrafları</button>
        </div>
        <div class="renao-live-preview-dock__slot" data-renao-music-live-preview-slot></div>
      `;
      mediaWrapper.insertBefore(dock, mediaWrapper.firstChild);
      dock.querySelector('[data-renao-music-show-product-media]')?.addEventListener('click', showProductMedia);
      return dock;
    };

    const showLivePreview = () => {
      if (!preview) return;
      const mediaWrapper = getProductMediaWrapper();
      if (!mediaWrapper) return;

      const dock = ensureLivePreviewDock(mediaWrapper);
      const slot = dock.querySelector('[data-renao-music-live-preview-slot]');
      if (!slot) return;

      if (preview.parentElement !== slot) slot.appendChild(preview);
      mediaWrapper.classList.remove('is-renao-media-returning');
      mediaWrapper.classList.add('has-renao-live-preview');
      root.classList.add('has-renao-live-preview');
      window.requestAnimationFrame(() => {
        syncPhotoTransform();
        fitTitle();
        window.requestAnimationFrame(() => {
          syncPhotoTransform();
          fitTitle();
        });
      });
      window.setTimeout(fitTitle, 260);
      window.setTimeout(fitTitle, 620);
    };

    const activateLivePreview = (event) => {
      if (event?.target?.closest?.('[data-renao-music-crop]')) return;
      showLivePreview();
    };

    const openProofModal = () => {
      if (!proofModal || !proofStage || !preview) return;
      const poster = preview.querySelector('.renao-music-poster');
      if (!poster) return;
      if (proofModal.parentElement !== document.body) document.body.appendChild(proofModal);
      proofStage.innerHTML = '';
      const proofPoster = poster.cloneNode(true);
      proofPoster.style.maxWidth = '100%';
      proofStage.appendChild(proofPoster);
      const proofPhoto = proofPoster.querySelector('[data-renao-music-photo-preview]');
      if (proofPhoto?.parentElement) {
        proofPhoto.style.transform = getPhotoTransformForStage(proofPhoto.parentElement);
        proofPhoto.style.transformOrigin = 'center';
      }
      const proofPanel = proofModal.querySelector('.renao-proof-modal__panel');
      if (proofPanel) proofPanel.scrollTop = 0;
      proofModal.hidden = false;
      window.requestAnimationFrame(() => {
        fitTitleElement(proofPoster.querySelector('[data-renao-music-title-preview]'));
        window.requestAnimationFrame(() => fitTitleElement(proofPoster.querySelector('[data-renao-music-title-preview]')));
      });
      document.documentElement.classList.add('renao-proof-modal-open');
    };

    const closeProofModal = () => {
      if (!proofModal) return;
      proofModal.hidden = true;
      document.documentElement.classList.remove('renao-proof-modal-open');
    };

    const focusInvalidField = (field, focusTarget) => {
      activateLivePreview();
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => focusTarget?.focus?.({ preventScroll: true }), 280);
    };

    const hasChosenPhoto = () => Boolean(
      objectUrl
      || existingPhotoUrl
      || (photoInput?.files && photoInput.files.length > 0)
    );

    const hasSpotifyCodeSource = () => Boolean(
      selectedSpotifyUri
      || normalizeSpotifyUrl(urlInput?.value || '')
    );

    const hasChosenSong = () => {
      if (isNoteMode) return true;
      if (providerInput?.value === 'Spotify') return hasSpotifyCodeSource();

      return Boolean(
        isSongConfirmed
        || (isManualLinkConfirmed && String(urlInput?.value || '').trim())
      );
    };

    const validateBeforeSubmit = (event) => {
      ensureDefaultNoteValue();
      const isPhotoMissing = !hasChosenPhoto();
      const isSongMissing = !isNoteMode && !hasChosenSong();
      const isPetNameMissing = Boolean(petNameInput) && !petNameInput.value.trim();

      root.classList.toggle('has-renao-music-photo-error', isPhotoMissing);
      root.classList.toggle('has-renao-music-song-error', isSongMissing);
      root.classList.toggle('has-renao-pet-name-error', isPetNameMissing);

      if (!isPhotoMissing && !isSongMissing && !isPetNameMissing) return true;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      const missingSongMessage = providerInput?.value === 'Spotify' && !hasSpotifyCodeSource()
        ? 'Spotify kodu için Spotify şarkı linki gir veya listeden Spotify sonucunu seç.'
        : 'Şarkıyı listeden seç veya link gir.';

      setStatus(isPhotoMissing
        ? 'Fotoğraf seçmeden sepete ekleyemezsin.'
        : isPetNameMissing
          ? 'Dost ismini yazmadan sepete ekleyemezsin.'
        : missingSongMessage);

      if (isPhotoMissing) {
        focusInvalidField(photoField, photoButton);
      } else if (isPetNameMissing) {
        focusInvalidField(petNameInput?.closest('.renao-music-field'), petNameInput);
      } else {
        if (providerInput?.value === 'Spotify' && !hasSpotifyCodeSource()) {
          if (linkField) linkField.hidden = false;
          if (linkToggle) linkToggle.textContent = 'Link alanını gizle';
        }
        focusInvalidField(songField, titleInput);
      }

      return false;
    };

    const clampZoom = (value) => Math.min(2.6, Math.max(1, Number(value || 1)));

    const getCropReferenceStage = () => {
      if (cropStage && !cropModal?.hidden && cropStage.clientWidth && cropStage.clientHeight) return cropStage;
      const previewStage = photoPreview?.parentElement;
      if (previewStage?.clientWidth && previewStage?.clientHeight) return previewStage;
      return null;
    };

    const clampPhotoPosition = () => {
      const stage = getCropReferenceStage();
      if (stage && cropStage && !cropModal?.hidden) {
        cropReferenceSize = { width: stage.clientWidth, height: stage.clientHeight };
      } else if (!cropReferenceSize.width && stage) {
        cropReferenceSize = { width: stage.clientWidth, height: stage.clientHeight };
      }

      const referenceWidth = cropReferenceSize.width || stage?.clientWidth || 0;
      const referenceHeight = cropReferenceSize.height || stage?.clientHeight || 0;
      if (!referenceWidth || !referenceHeight) return;
      if (!cropState.normalizedPositionApplied
        && Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)) {
        cropState.x = cropState.normalizedX * referenceWidth;
        cropState.y = cropState.normalizedY * referenceHeight;
        cropState.normalizedPositionApplied = true;
      }
      const maxX = Math.max(0, (referenceWidth * (cropState.scale - 1)) / 2);
      const maxY = Math.max(0, (referenceHeight * (cropState.scale - 1)) / 2);
      cropState.x = Math.min(maxX, Math.max(-maxX, cropState.x));
      cropState.y = Math.min(maxY, Math.max(-maxY, cropState.y));
      cropState.normalizedX = cropState.x / referenceWidth;
      cropState.normalizedY = cropState.y / referenceHeight;
    };

    const getPhotoTransformForStage = (targetStage) => {
      const referenceWidth = cropReferenceSize.width || cropStage?.clientWidth || targetStage?.clientWidth || 1;
      const referenceHeight = cropReferenceSize.height || cropStage?.clientHeight || targetStage?.clientHeight || 1;
      const ratioX = targetStage?.clientWidth ? targetStage.clientWidth / referenceWidth : 1;
      const ratioY = targetStage?.clientHeight ? targetStage.clientHeight / referenceHeight : 1;
      return `translate(${cropState.x * ratioX}px, ${cropState.y * ratioY}px) scale(${cropState.scale})`;
    };

    const syncPhotoTransform = () => {
      clampPhotoPosition();
      if (photoPreview) {
        const previewStage = photoPreview.parentElement || cropStage;
        photoPreview.style.transform = previewStage ? getPhotoTransformForStage(previewStage) : `scale(${cropState.scale})`;
        photoPreview.style.transformOrigin = 'center';
      }
      if (cropImage) {
        const imageStage = cropStage || cropImage.parentElement;
        cropImage.style.transform = imageStage ? getPhotoTransformForStage(imageStage) : `scale(${cropState.scale})`;
        cropImage.style.transformOrigin = 'center';
      }
      if (photoPosition) {
        const normalized = Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)
          ? `, nx:${cropState.normalizedX.toFixed(6)}, ny:${cropState.normalizedY.toFixed(6)}`
          : '';
        const legacy = `x:${Math.round(cropState.x)}, y:${Math.round(cropState.y)}, zoom:${cropState.scale.toFixed(2)}${normalized}`;
        photoPosition.value = globalThis.RenaoCropRecipe?.merge
          ? globalThis.RenaoCropRecipe.merge(photoPosition.value, legacy, photoPreview, photoPreview?.parentElement)
          : legacy;
      }
    };

    const openCrop = () => {
      const cropSource = getDisplayPhotoUrl();
      if (!cropModal || !objectUrl || !cropSource) return;
      cropModal.hidden = false;
      document.documentElement.classList.add('renao-music-crop-open');
      if (cropImage) cropImage.src = cropSource;
      if (cropZoom) cropZoom.value = cropState.scale;
      syncPhotoTransform();
    };

    const closeCrop = () => {
      if (!cropModal) return;
      cropModal.hidden = true;
      document.documentElement.classList.remove('renao-music-crop-open');
      dragState = null;
    };

    const setZoom = (nextZoom) => {
      cropState.scale = clampZoom(nextZoom);
      if (cropZoom) cropZoom.value = cropState.scale;
      syncPhotoTransform();
    };

    const moveCrop = (direction, amount = 14) => {
      if (direction === 'up') cropState.y -= amount;
      if (direction === 'down') cropState.y += amount;
      if (direction === 'left') cropState.x -= amount;
      if (direction === 'right') cropState.x += amount;
      syncPhotoTransform();
    };

    const fitTitleElement = (titleElement) => {
      if (!titleElement) return;
      titleElement.style.fontSize = '';
      window.requestAnimationFrame(() => {
        const baseSize = Number.parseFloat(window.getComputedStyle(titleElement).fontSize) || 14;
        const minSize = Math.max(3.2, baseSize * 0.34);
        let nextSize = baseSize;
        while (titleElement.scrollWidth > titleElement.clientWidth && nextSize > minSize) {
          nextSize -= 0.5;
          titleElement.style.fontSize = `${nextSize}px`;
        }
      });
    };

    const fitTitle = () => fitTitleElement(titlePreview);

    const isSignatureEnabled = () => !signatureToggle || signatureToggle.checked;
    const isPetNoteEnabled = () => !petNoteToggle || petNoteToggle.checked;
    const isPetDateEnabled = () => !petDateToggle || petDateToggle.checked;
    const petNameFontFallbacks = {
      amsterdam: 'Amsterdam',
      'new-york': 'New York',
      '00619': '00619',
    };

    const setPetNameFont = (fontValue = 'amsterdam') => {
      const nextFont = petNameFontFallbacks[fontValue] ? fontValue : 'amsterdam';
      const nextLabel = petNameFontFallbacks[nextFont];
      if (petNamePoster) petNamePoster.dataset.renaoPetNameFont = nextFont;
      if (petNameFontProperty) petNameFontProperty.value = nextLabel;
      petNameFontOptions.forEach((button) => {
        const isSelected = button.dataset.renaoPetNameFontValue === nextFont;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const syncPetName = () => {
      if (!petNameInput && !petNamePreview) return;
      const maxLength = Number(petNameInput?.getAttribute('maxlength')) || 22;
      if (petNameInput && petNameInput.value.length > maxLength) {
        petNameInput.value = petNameInput.value.slice(0, maxLength);
      }

      const petName = petNameInput?.value.trim() || petNameInput?.placeholder || 'Pamuk';
      if (petNamePreview) petNamePreview.textContent = petName;
      if (petNameInput?.value.trim()) root.classList.remove('has-renao-pet-name-error');
    };

    const syncPetDate = () => {
      if (!petDateInput && !petDatePreview) return;
      const isEnabled = isPetDateEnabled();
      const maxLength = Number(petDateInput?.getAttribute('maxlength')) || 10;
      if (petDateInput) {
        const dateValue = petDateInput.value.replace(/[^0-9-]/g, '');
        const hyphenIndex = dateValue.indexOf('-');
        const dateWithSingleHyphen = hyphenIndex === -1
          ? dateValue
          : `${dateValue.slice(0, hyphenIndex + 1)}${dateValue.slice(hyphenIndex + 1).replace(/-/g, '')}`;
        const sanitizedValue = dateWithSingleHyphen.slice(0, maxLength);
        if (petDateInput.value !== sanitizedValue) petDateInput.value = sanitizedValue;
      }

      if (petDateInput) {
        petDateInput.disabled = !isEnabled;
        if (isEnabled) {
          petDateInput.setAttribute('name', 'properties[Tarih]');
        } else {
          petDateInput.removeAttribute('name');
        }
      }

      if (petDatePreview) {
        petDatePreview.hidden = !isEnabled;
        petDatePreview.textContent = petDateInput?.value.trim() || petDateInput?.placeholder || '2018-2022';
      }
    };

    const normalizeSignatureInput = () => {
      if (!signatureInput) return '';
      const maxLength = Number(signatureInput.getAttribute('maxlength')) || 20;
      const formattedValue = toTitleNameLoose(signatureInput.value).slice(0, maxLength);
      if (signatureInput.value !== formattedValue) {
        const selectionStart = signatureInput.selectionStart;
        const selectionEnd = signatureInput.selectionEnd;
        signatureInput.value = formattedValue;
        if (document.activeElement === signatureInput && selectionStart !== null && selectionEnd !== null) {
          signatureInput.setSelectionRange(selectionStart, selectionEnd);
        }
      }
      return signatureInput.value.trim();
    };

    const syncSignature = () => {
      if (!signatureInput && !signaturePreview) return;
      const isEnabled = isSignatureEnabled();
      const signature = normalizeSignatureInput()
        || signatureInput?.placeholder
        || 'Ada Tekin';

      if (signatureInput) {
        if (isEnabled) {
          signatureInput.setAttribute('name', 'properties[İsim Soyisim]');
        } else {
          signatureInput.removeAttribute('name');
        }
        signatureInput.disabled = !isEnabled;
      }

      if (signaturePreview) {
        signaturePreview.hidden = !isEnabled;
        signaturePreview.textContent = `- ${signature}`;
      }
    };

    const syncNote = () => {
      const petNoteEnabled = isPetNoteEnabled();
      if (noteInput && petNoteToggle) {
        noteInput.disabled = !petNoteEnabled;
        if (petNoteEnabled) {
          noteInput.setAttribute('name', 'properties[Not]');
        } else {
          noteInput.removeAttribute('name');
        }
      }

      if (noteCount && noteInput) {
        const maxLength = Number(noteInput.getAttribute('maxlength')) || 180;
        if (noteInput.value.length > maxLength) {
          noteInput.value = noteInput.value.slice(0, maxLength);
        }
        noteCount.textContent = `${noteInput.value.length}/${maxLength}`;
      }
      if (!notePreview) return;
      const note = noteInput?.value.trim() || noteInput?.placeholder || DEFAULT_NOTE;
      const lines = note
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      const serifLines = lines.length
        ? lines
        : DEFAULT_NOTE.split('\n');

      if (noteSerifPreview) {
        noteSerifPreview.hidden = !petNoteEnabled;
        noteSerifPreview.textContent = serifLines.join('\n');
        syncPetName();
        syncPetDate();
        syncSignature();
        return;
      }

      notePreview.textContent = note;
      syncPetName();
      syncPetDate();
      syncSignature();
    };

    const ensureDefaultNoteValue = () => {
      if (isPetFriendMode && !isPetNoteEnabled()) return;
      if (!noteInput || noteInput.value.trim()) return;
      noteInput.value = noteInput.placeholder || DEFAULT_NOTE;
      syncNote();
    };

    const timeToSeconds = (value) => {
      const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return 0;
      return (Number(match[1]) * 60) + Number(match[2]);
    };

    const syncProgress = () => {
      if (!progressDot) return;
      const currentSeconds = timeToSeconds(currentInput?.value || DEFAULT_CURRENT);
      const durationSeconds = timeToSeconds(durationInput?.value || DEFAULT_DURATION);
      const ratio = durationSeconds > 0 ? Math.min(1, Math.max(0, currentSeconds / durationSeconds)) : 0.5;
      const minPercent = 2.02;
      const maxPercent = 100 - minPercent;
      const percent = minPercent + (ratio * (maxPercent - minPercent));
      progressDot.style.left = `${percent}%`;
    };

    const sanitizeCurrentInput = ({ complete = false } = {}) => {
      if (!currentInput) return;
      const nextValue = sanitizeTimeValue(currentInput.value);
      if (currentInput.value !== nextValue) currentInput.value = nextValue;
      if (complete && currentInput.value && !isCompleteTimeValue(currentInput.value)) {
        currentInput.value = DEFAULT_CURRENT;
      }
    };

    const uppercaseTitleInput = () => {
      const nextValue = titleInput.value.normalize('NFC').toLocaleUpperCase('tr-TR');
      if (titleInput.value === nextValue) return;
      const selectionStart = titleInput.selectionStart;
      const selectionEnd = titleInput.selectionEnd;
      titleInput.value = nextValue;
      if (document.activeElement === titleInput && selectionStart !== null && selectionEnd !== null) {
        titleInput.setSelectionRange(selectionStart, selectionEnd);
      }
    };

    const syncText = () => {
      if (isNoteMode) {
        syncNote();
        return;
      }

      if (titleInput.value !== clampTitleValue(titleInput.value)) {
        titleInput.value = clampTitleValue(titleInput.value);
      }
      const hasManualYouTubeUrl = providerInput?.value === 'YouTube' && Boolean(normalizeYouTubeUrl(urlInput?.value));
      const youtubeFallbackTitle = youtubeMetadataStatus === 'loading'
        ? 'ŞARKI BİLGİSİ ALINIYOR'
        : 'ŞARKI ADINI YAZIN';
      const title = titleInput.value.trim().normalize('NFC')
        || selectedTrackTitle
        || (hasManualYouTubeUrl ? youtubeFallbackTitle : DEFAULT_TITLE);
      const current = currentInput?.value.trim() || DEFAULT_CURRENT;
      const duration = durationInput?.value.trim() || DEFAULT_DURATION;

      renderTitlePreview(titlePreview, title.toLocaleUpperCase('tr-TR'));
      if (currentPreview) currentPreview.textContent = current;
      if (durationPreview) durationPreview.textContent = duration;
      syncProgress();
      fitTitle();
    };

    const getSongUrl = () => {
      if (isNoteMode || !urlInput || !titleInput) return '';
      const manualUrl = urlInput.value.trim();
      const title = titleInput.value.trim() || selectedTrackTitle;
      const compactQuery = compactSearchQuery(title);
      const youtubeSearchUrl = compactQuery ? `https://m.youtube.com/results?search_query=${compactQuery}` : '';
      if (!manualUrl) return youtubeSearchUrl;
      if (normalizeSpotifyUrl(manualUrl)) return youtubeSearchUrl;
      return manualUrl;
    };

    const shortenUrl = async (targetUrl) => {
      if (!targetUrl) return '';
      if (shortUrlCache.has(targetUrl)) return shortUrlCache.get(targetUrl);

      const shortenUrlEndpoint = new URL(getShortenUrl(), window.location.origin);
      shortenUrlEndpoint.searchParams.set('url', targetUrl);
      const response = await fetch(shortenUrlEndpoint.toString(), { headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok || !data?.success || !data?.shortUrl) {
        throw new Error(data?.error || 'Kisa link alinamadi.');
      }

      const shortUrl = new URL(data.shortUrl, window.location.origin);
      const endpointUrl = new URL(getShortenUrl(), window.location.origin);
      const normalizedShortUrl = shortUrl.pathname.startsWith('/apps/renao-spotify/r/') && endpointUrl.origin !== window.location.origin
        ? `${endpointUrl.origin}${shortUrl.pathname}`
        : shortUrl.toString();

      shortUrlCache.set(targetUrl, normalizedShortUrl);
      return normalizedShortUrl;
    };

    const fetchYouTubeMetadata = async (targetUrl) => {
      const response = await fetch(getYouTubeMetadataUrl(targetUrl), { headers: { Accept: 'application/json' } });
      const data = await response.json();
      const title = String(data?.title || '').trim();
      if (!response.ok || data?.error || !title) {
        throw new Error(data?.error || 'YouTube video bilgisi alınamadı.');
      }
      return { title: clampTitleValue(title) };
    };

    const scheduleYouTubeMetadata = () => {
      window.clearTimeout(youtubeMetadataTimer);
      const targetUrl = providerInput?.value === 'YouTube' ? normalizeYouTubeUrl(urlInput?.value) : '';
      const runId = ++youtubeMetadataRun;

      if (!targetUrl) {
        youtubeMetadataStatus = 'idle';
        syncText();
        return;
      }

      youtubeMetadataStatus = 'loading';
      syncText();
      setStatus('YouTube şarkı bilgisi alınıyor...');
      youtubeMetadataTimer = window.setTimeout(() => {
        fetchYouTubeMetadata(targetUrl)
          .then(({ title }) => {
            if (runId !== youtubeMetadataRun || normalizeYouTubeUrl(urlInput?.value) !== targetUrl || providerInput?.value !== 'YouTube') return;
            youtubeMetadataStatus = 'resolved';
            if (!titleInput.value.trim()) {
              titleInput.value = title;
              uppercaseTitleInput();
            }
            selectedTrackTitle = titleInput.value.trim();
            isSongConfirmed = Boolean(selectedTrackTitle);
            root.classList.remove('has-renao-music-song-error');
            syncSelectedTrackNotice();
            syncText();
            setStatus('YouTube şarkı bilgisi otomatik dolduruldu.');
          })
          .catch(() => {
            if (runId !== youtubeMetadataRun || normalizeYouTubeUrl(urlInput?.value) !== targetUrl || providerInput?.value !== 'YouTube') return;
            youtubeMetadataStatus = 'error';
            syncText();
            setStatus('Video adı alınamadı; baskıda görünecek şarkı adını elle yazın.');
          });
      }, 450);
    };

    const syncCode = () => {
      if (isNoteMode || !providerInput) {
        if (codeProperty) codeProperty.value = '';
        return;
      }

      const provider = providerInput.value;
      const manualUrl = urlInput.value.trim();
      let codeUrl = '';
      const runId = ++qrRun;

      if (provider === 'Spotify') {
        setCodeFallbackProvider('Spotify');
        const spotifyUri = selectedSpotifyUri || normalizeSpotifyUrl(manualUrl);
        codeUrl = spotifyUri ? getSpotifyCodeUrl(spotifyUri) : '';
        setImage(codeImage, codeFallback, codeWrap, codeUrl, false);
      } else {
        setCodeFallbackProvider('YouTube');
        const qrTarget = getSongUrl() || 'https://renaogift.com';
        codeUrl = qrTarget ? getStyledQrUrl(qrTarget) : '';
        setImage(codeImage, codeFallback, codeWrap, codeUrl, true);
        shortenUrl(qrTarget)
          .then((shortTarget) => {
            if (runId !== qrRun || providerInput.value !== 'YouTube') return;
            const shortCodeUrl = getStyledQrUrl(shortTarget);
            setImage(codeImage, codeFallback, codeWrap, shortCodeUrl, true);
            if (codeProperty) codeProperty.value = shortCodeUrl;
          })
          .catch(() => {
            if (runId === qrRun) setStatus('QR kod hazır.');
          });
      }

      if (codeProperty) codeProperty.value = codeUrl;
    };

    const setProvider = (provider) => {
      if (!providerInput) return;

      providerInput.value = provider;
      const poster = preview?.querySelector('.renao-music-poster');
      if (poster) {
        poster.classList.remove('is-code-changing');
        void poster.offsetWidth;
        poster.classList.add('is-code-changing');
        window.setTimeout(() => poster.classList.remove('is-code-changing'), 340);
      }
      providerOptions.forEach((button) => {
        const isSelected = button.dataset.renaoMusicProviderOption === provider;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
      syncCode();
      scheduleYouTubeMetadata();
      setStatus(provider === 'Spotify'
        ? 'Spotify kodu için link girersen gerçek Spotify kodu oluşur.'
        : 'YouTube QR kodu oluşur.');
    };

    codeImage?.addEventListener('load', () => {
      if (codeImage.hidden) return;
      window.requestAnimationFrame(fitTitle);
      setStatus(codeWrap?.classList.contains('is-qr') ? 'YouTube QR kodu oluşur.' : 'Spotify kodu hazır.');
    });

    codeImage?.addEventListener('error', () => {
      codeImage.hidden = true;
      codeImage.removeAttribute('src');
      if (codeFallback) codeFallback.hidden = false;
      setStatus(providerInput.value === 'Spotify'
        ? 'Spotify kodu alınamadı; linki veya servis ayarını kontrol et.'
        : 'QR kod hazırlanamadı; linki kontrol et.');
    });

    const hideResults = () => {
      if (!results) return;
      results.hidden = true;
      results.innerHTML = '';
    };

    const selectedTrackNotice = document.createElement('div');
    selectedTrackNotice.className = 'renao-music-selected';
    selectedTrackNotice.hidden = true;
    selectedTrackNotice.innerHTML = '<span></span><button type="button">Kaldır</button>';
    results?.insertAdjacentElement('afterend', selectedTrackNotice);
    const selectedTrackNoticeText = selectedTrackNotice.querySelector('span');
    const selectedTrackClear = selectedTrackNotice.querySelector('button');

    const syncSelectedTrackNotice = () => {
      if (selectedTrackInput) selectedTrackInput.value = selectedTrackTitle;
      if (!selectedTrackNoticeText) return;
      selectedTrackNotice.hidden = !selectedTrackTitle;
      selectedTrackNoticeText.textContent = selectedTrackTitle ? `Seçili şarkı: ${selectedTrackTitle}` : '';
    };

    const clearSelectedTrack = () => {
      selectedSpotifyUri = '';
      selectedTrackTitle = '';
      isSongConfirmed = false;
      if (!isManualLinkConfirmed && urlInput) urlInput.value = '';
      syncSelectedTrackNotice();
      syncCode();
      setStatus('Şarkı adını yazınca öneriler otomatik gelir.');
    };

    const setVisualStyle = (value) => {
      if (!visualStyleInput) return;
      const selectedValue = value || 'Fotoğraf olarak kalsın';
      visualStyleInput.value = selectedValue;
      visualStyleOptions.forEach((button) => {
        const isSelected = button.dataset.renaoVisualStyleOption === selectedValue;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
      if (visualStyleNote) {
        if (selectedValue === 'Animasyon tasarım yapılsın') {
          visualStyleNote.textContent = 'Animasyon tasarım seçildi. Tasarımcılarımız fotoğrafınızı çizim tarzına çevirip üretime hazırlar.';
        } else if (selectedValue === 'One line çizim yapılsın') {
          visualStyleNote.textContent = 'One line çizim seçildi. Önizleme fotoğraf yerleşimini gösterir; nihai çizim tasarım ekibimiz tarafından hazırlanır.';
        } else {
          visualStyleNote.textContent = 'Fotoğraf olarak kalsın seçildi. Yüklediğiniz fotoğraf baskı önizlemesindeki gibi kullanılır.';
        }
      }
    };

    const selectTrack = (track) => {
      const title = formatTrackTitle(track);
      if (title) titleInput.value = clampTitleValue(title);
      uppercaseTitleInput();
      if (durationInput && track.trackTimeMillis) durationInput.value = formatDuration(track.trackTimeMillis);
      selectedSpotifyUri = track.spotifyUri || normalizeSpotifyUrl(track.spotifyUrl || '');
      selectedTrackTitle = title;
      isSongConfirmed = true;
      isManualLinkConfirmed = false;
      root.classList.remove('has-renao-music-song-error');
      if (providerInput.value === 'Spotify') {
        urlInput.value = track.spotifyUrl || track.spotifyUri || '';
        if (!selectedSpotifyUri && linkField && linkToggle) {
          linkField.hidden = false;
          linkToggle.textContent = 'Link alanını gizle';
        }
      }
      hideResults();
      syncSelectedTrackNotice();
      syncText();
      syncCode();
      setStatus(track.provider === 'Spotify'
        ? 'Spotify kodu ve şarkı bilgileri otomatik dolduruldu.'
        : 'Şarkı bilgileri doldu; gerçek Spotify kodu için Spotify linki gerekir.');
    };

    const renderResults = (tracks) => {
      if (!results) return;
      results.innerHTML = '';

      if (!tracks.length) {
        results.hidden = true;
        setStatus('Şarkı bulunamadı; link alanını açıp elle girebilirsin.');
        return;
      }

      tracks.forEach((track) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'renao-music-result';

        const art = document.createElement('span');
        art.className = 'renao-music-result__art';
        if (track.artworkUrl60) {
          const image = document.createElement('img');
          image.src = track.artworkUrl60;
          image.alt = '';
          art.appendChild(image);
        }

        const copy = document.createElement('span');
        const title = document.createElement('strong');
        const meta = document.createElement('em');
        title.textContent = track.trackName || 'Şarkı';
        meta.textContent = `${track.provider || 'Yedek arama'} · ${track.artistName || ''}${track.trackTimeMillis ? ` - ${formatDuration(track.trackTimeMillis)}` : ''}`;
        copy.append(title, meta);
        button.append(art, copy);
        button.addEventListener('click', () => selectTrack(track));
        results.appendChild(button);
      });

      results.hidden = false;
      setStatus('Listeden şarkıyı seçince süre otomatik gelir.');
    };

    const fetchSpotifyTracks = (term) => {
      const spotifySearchUrl = getSpotifySearchUrl();
      if (!spotifySearchUrl) return Promise.resolve([]);

      const url = new URL(spotifySearchUrl, window.location.origin);
      url.searchParams.set('q', term);

      return fetch(url.toString())
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Spotify aramasi yapilamadi')))
        .then((data) => Array.isArray(data.tracks) ? data.tracks : [])
        .catch(() => []);
    };

    const fetchItunesTracks = (term) => (
      fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(term)}&media=music&entity=song&limit=6&country=TR`)
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Arama yapılamadı')))
        .then((data) => Array.isArray(data.results) ? data.results : [])
    );

    const searchSongs = () => {
      const term = titleInput.value.trim();
      if (term.length < 2) {
        hideResults();
        setStatus('Şarkı adını yazınca öneriler otomatik gelir.');
        syncText();
        syncCode();
        return;
      }

      const runId = ++searchRun;
      setStatus('Şarkılar aranıyor...');

      fetchSpotifyTracks(term)
        .then((tracks) => tracks.length ? tracks : fetchItunesTracks(term))
        .catch(() => fetchItunesTracks(term))
        .then((tracks) => {
          if (runId !== searchRun) return;
          renderResults(tracks);
        })
        .catch(() => {
          if (runId !== searchRun) return;
          hideResults();
          setStatus('Arama yapılamadı; link alanını açıp elle girebilirsin.');
        });
    };

    const normalizeCartEditProperties = (properties) => {
      if (!properties) return {};
      const normalizeEntry = (result, key, value) => {
        result[decodeHtmlEntities(key)] = decodeHtmlEntities(value);
        return result;
      };

      if (!Array.isArray(properties)) {
        return Object.entries(properties).reduce((result, [key, value]) => {
          if (Array.isArray(value) && value.length >= 2) return normalizeEntry(result, value[0], value[1]);
          if (value && typeof value === 'object' && value.first) return normalizeEntry(result, value.first, value.last);
          return normalizeEntry(result, key, value);
        }, {});
      }

      return properties.reduce((result, property) => {
        if (Array.isArray(property) && property.length >= 2) normalizeEntry(result, property[0], property[1]);
        if (property && typeof property === 'object' && property.first) normalizeEntry(result, property.first, property.last);
        return result;
      }, {});
    };

    const getCartEditKeyFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get('renao_edit');
    };

    const getCartEditState = () => {
      const editKey = getCartEditKeyFromUrl();
      if (!editKey) return null;

      try {
        const savedState = JSON.parse(window.sessionStorage.getItem(`renaoCartEdit:${editKey}`) || 'null');
        if (!savedState) return null;
        savedState.key = savedState.key || editKey;
        savedState.properties = normalizeCartEditProperties(savedState.properties);
        return savedState;
      } catch (error) {
        console.warn('Renao music cart edit state could not be restored.', error);
        return null;
      }
    };

    const fetchCartEditState = (editKey) => {
      if (!editKey) return Promise.resolve(null);

      return fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.ok ? response.json() : null)
        .then((cartState) => {
          const item = cartState?.items?.find((cartItem) => cartItem.key === editKey);
          if (!item) return null;

          return {
            key: item.key,
            quantity: item.quantity,
            variantId: item.variant_id,
            properties: normalizeCartEditProperties(item.properties),
          };
        })
        .catch((error) => {
          console.warn('Renao music cart edit state could not be fetched.', error);
          return null;
        });
    };

    const setEditSubmitText = (productForm, attempt = 0) => {
      if (!productForm) return;
      const submitButtonText = productForm.querySelector('[type="submit"] span');
      if (submitButtonText) {
        submitButtonText.textContent = 'Düzenlemeyi Bitir';
        return;
      }
      if (attempt < 12) window.setTimeout(() => setEditSubmitText(productForm, attempt + 1), 80);
    };

    const applyGiftWrapEditValue = (productForm, value) => {
      if (!productForm || value === undefined) return;
      const giftWrapSelect = productForm.querySelector('[data-renao-gift-wrap-select]');
      if (!giftWrapSelect) return;

      const option = Array.from(giftWrapSelect.options).find((item) => item.value === value);
      if (option) giftWrapSelect.value = option.value;
    };

    const applyPhotoPositionValue = (value) => {
      const text = String(value || '');
      if (photoPosition) photoPosition.value = text;
      const x = text.match(/x:([-.\d]+)/);
      const y = text.match(/y:([-.\d]+)/);
      const zoom = text.match(/zoom:([-.\d]+)/);
      const normalizedX = text.match(/(?:^|[,;]\s*)nx:([-.\d]+)/);
      const normalizedY = text.match(/(?:^|[,;]\s*)ny:([-.\d]+)/);

      cropState = {
        x: x ? Number.parseFloat(x[1]) || 0 : cropState.x,
        y: y ? Number.parseFloat(y[1]) || 0 : cropState.y,
        scale: zoom ? clampZoom(Number.parseFloat(zoom[1]) || 1) : cropState.scale,
        normalizedX: normalizedX ? Number.parseFloat(normalizedX[1]) : Number.NaN,
        normalizedY: normalizedY ? Number.parseFloat(normalizedY[1]) : Number.NaN,
        normalizedPositionApplied: false,
      };
    };

    const applyCartEditState = (editState) => {
      if (!editState || !editState.properties) return;
      const props = editState.properties;
      const productForm = getProductForm();

      if (productForm) {
        productForm.dataset.renaoEditKey = editState.key || '';
        productForm.dataset.renaoEditStorageKey = editState.key ? `renaoCartEdit:${editState.key}` : '';
        setEditSubmitText(productForm);
      }

      if (props['Kod Tipi']) setProvider(props['Kod Tipi']);
      if (titleInput && props['Şarkı Adı']) titleInput.value = clampTitleValue(props['Şarkı Adı']);
      if (urlInput && props['Şarkı Linki']) {
        urlInput.value = props['Şarkı Linki'];
        isManualLinkConfirmed = true;
        if (linkField) linkField.hidden = false;
        if (linkToggle) linkToggle.textContent = 'Link alanını gizle';
      }
      if (currentInput && props['Başlangıç Zamanı']) currentInput.value = props['Başlangıç Zamanı'];
      if (durationInput && props['Şarkı Süresi']) durationInput.value = props['Şarkı Süresi'];
      if (props['_Fotoğraf Konumu']) applyPhotoPositionValue(props['_Fotoğraf Konumu']);
      const existingPhoto = props._Fotoğraf || props.Fotoğraf; if (existingPhoto && String(existingPhoto).includes('/uploads/')) setExistingPhoto(existingPhoto);
      if (props['Görsel Tercihi']) setVisualStyle(props['Görsel Tercihi']);
      if (petNameInput && props['Dost İsmi'] !== undefined) petNameInput.value = props['Dost İsmi'];
      if (props['İsim Fontu']) {
        const fontKey = Object.entries(petNameFontFallbacks).find(([, label]) => label === props['İsim Fontu'])?.[0];
        setPetNameFont(fontKey || props['İsim Fontu']);
      }
      if (petNoteToggle && props.Not === undefined) petNoteToggle.checked = false;
      if (petDateInput && props.Tarih !== undefined) petDateInput.value = props.Tarih;
      if (petDateToggle && props.Tarih === undefined) petDateToggle.checked = false;
      if (signatureInput) {
        const hasSignature = props['İsim Soyisim'] !== undefined;
        signatureInput.value = hasSignature ? props['İsim Soyisim'] : '';
        if (signatureToggle) signatureToggle.checked = hasSignature;
        syncSignature();
      }

      selectedTrackTitle = props['_Seçilen Şarkı'] || props['Şarkı Adı'] || '';
      selectedSpotifyUri = normalizeSpotifyUrl(props['Şarkı Linki'] || '');
      isSongConfirmed = Boolean(selectedTrackTitle || selectedSpotifyUri);
      isManualLinkConfirmed = Boolean(isManualLinkConfirmed || props['Şarkı Linki']);

	      const orderNote = productForm?.querySelector('[data-renao-order-note]');
	      if (noteInput && props.Not !== undefined) noteInput.value = props.Not;
	      if (orderNote) {
	        const orderNoteValue = isNoteMode ? props['Sipariş Notu'] : props.Not;
	        if (orderNoteValue !== undefined) orderNote.value = orderNoteValue;
	      }
	      applyGiftWrapEditValue(productForm, props['Hediye Paketi']);

      root.classList.remove('has-renao-music-photo-error', 'has-renao-music-song-error', 'has-renao-pet-name-error');
      syncSelectedTrackNotice();
      syncText();
      syncCode();
      syncPhotoTransform();
      setStatus('Düzenleme modu açık; değişiklikleri bitirince sepet güncellenecek.');
      scheduleYouTubeMetadata();
    };

    photoButton?.addEventListener('click', () => photoInput?.click());
    photoAdjust?.addEventListener('click', openCrop);
    visualStyleOptions.forEach((button) => {
      button.addEventListener('click', () => {
        setVisualStyle(button.dataset.renaoVisualStyleOption);
        activateLivePreview();
      });
    });
    root.addEventListener('pointerdown', activateLivePreview, { capture: true });
    root.addEventListener('focusin', activateLivePreview);
    root.addEventListener('input', activateLivePreview);
    root.addEventListener('change', activateLivePreview);

    photoInput?.addEventListener('change', () => {
      const file = photoInput.files && photoInput.files[0];
      if (!file && existingPhotoUrl) {
        root.classList.remove('has-renao-music-photo-error');
        return;
      }

      setExistingPhoto('');
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = file ? URL.createObjectURL(file) : '';
      cropState = { scale: 1, x: 0, y: 0 };

      setPhotoPreviewSource(objectUrl);
      if (photoPlaceholder) photoPlaceholder.hidden = Boolean(objectUrl);
      if (photoAdjust) photoAdjust.hidden = !objectUrl;
      root.classList.toggle('has-renao-music-photo-error', !objectUrl);
      syncPhotoTransform();
      if (objectUrl) {
        openCrop();
      }
    });

    cropStage?.addEventListener('pointerdown', (event) => {
      if (!objectUrl) return;
      event.preventDefault();
      event.stopPropagation();
      cropStage.setPointerCapture(event.pointerId);
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: cropState.x,
        originY: cropState.y,
      };
    });

    cropStage?.addEventListener('pointermove', (event) => {
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      cropState.x = dragState.originX + event.clientX - dragState.startX;
      cropState.y = dragState.originY + event.clientY - dragState.startY;
      syncPhotoTransform();
    });

    cropStage?.addEventListener('pointerup', (event) => {
      event.preventDefault();
      if (dragState?.pointerId === event.pointerId) dragState = null;
    });

    cropStage?.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    cropStage?.addEventListener('pointercancel', () => {
      dragState = null;
    });

    cropStage?.addEventListener('lostpointercapture', () => {
      dragState = null;
    });

    cropStage?.addEventListener('wheel', (event) => {
      if (!objectUrl) return;
      event.preventDefault();
      const step = event.deltaY > 0 ? -0.08 : 0.08;
      setZoom(cropState.scale + step);
    }, { passive: false });

    cropZoom?.addEventListener('input', () => {
      setZoom(cropZoom.value);
    });

    cropMoveButtons.forEach((button) => {
      button.addEventListener('click', () => moveCrop(button.dataset.renaoMusicCropMove));
    });

    cropZoomStepButtons.forEach((button) => {
      button.addEventListener('click', () => setZoom(cropState.scale + Number(button.dataset.renaoMusicCropZoomStep || 0)));
    });

    cropReset?.addEventListener('click', () => {
      cropState = { scale: 1, x: 0, y: 0 };
      setZoom(1);
    });

    cropApply?.addEventListener('click', closeCrop);
    cropCancelButtons.forEach((button) => button.addEventListener('click', closeCrop));
    proofOpen?.addEventListener('click', () => {
      showLivePreview();
      openProofModal();
    });
    proofCloseButtons.forEach((button) => button.addEventListener('click', closeProofModal));

    providerOptions.forEach((button) => {
      button.addEventListener('click', () => setProvider(button.dataset.renaoMusicProviderOption));
    });

    linkToggle?.addEventListener('click', () => {
      if (!linkField) return;
      linkField.hidden = !linkField.hidden;
      linkToggle.textContent = linkField.hidden ? 'Şarkıyı bulamadım, link gireceğim' : 'Link alanını gizle';
      if (!linkField.hidden) urlInput.focus();
    });

    noteInput?.addEventListener('input', syncNote);
    petNameInput?.addEventListener('input', () => {
      syncPetName();
      activateLivePreview();
    });
    petNameFontOptions.forEach((button) => {
      button.addEventListener('click', () => {
        setPetNameFont(button.dataset.renaoPetNameFontValue);
        activateLivePreview();
      });
    });
    petNoteToggle?.addEventListener('change', () => {
      syncNote();
      activateLivePreview();
    });
    petDateInput?.addEventListener('input', () => {
      syncPetDate();
      activateLivePreview();
    });
    petDateToggle?.addEventListener('change', () => {
      syncPetDate();
      activateLivePreview();
    });
    signatureInput?.addEventListener('input', syncNote);
    signatureToggle?.addEventListener('change', () => {
      syncNote();
      activateLivePreview();
    });

    urlInput?.addEventListener('input', () => {
      selectedSpotifyUri = '';
      selectedTrackTitle = '';
      isSongConfirmed = false;
      isManualLinkConfirmed = Boolean(urlInput.value.trim());
      syncSelectedTrackNotice();
      root.classList.toggle('has-renao-music-song-error', !hasChosenSong());
      scheduleYouTubeMetadata();
      syncCode();
    });

    titleInput?.addEventListener('input', () => {
      titleInput.value = clampTitleValue(titleInput.value);
      uppercaseTitleInput();
      syncText();
      syncCode();

      if (isSongConfirmed) {
        hideResults();
        root.classList.remove('has-renao-music-song-error');
        setStatus('Şarkı seçili; bu alan baskıda görünen yazıdır.');
        return;
      }

      selectedSpotifyUri = '';
      selectedTrackTitle = '';
      if (!isManualLinkConfirmed && urlInput) urlInput.value = '';
      syncSelectedTrackNotice();
      root.classList.add('has-renao-music-song-error');
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(searchSongs, 380);
    });

    selectedTrackClear?.addEventListener('click', clearSelectedTrack);

    currentInput?.addEventListener('input', () => {
      sanitizeCurrentInput();
      syncText();
    });
    currentInput?.addEventListener('blur', () => {
      sanitizeCurrentInput({ complete: true });
      syncText();
    });
    durationInput?.addEventListener('input', syncText);

    let resizeRun = 0;
    window.addEventListener('resize', () => {
      window.cancelAnimationFrame(resizeRun);
      resizeRun = window.requestAnimationFrame(() => {
        syncPhotoTransform();
        fitTitle();
      });
    });

    const productForm = root.closest('form');
    if (productForm) {
      productForm.addEventListener('submit', validateBeforeSubmit, true);
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && proofModal && !proofModal.hidden) closeProofModal();
      if (event.key === 'Escape' && cropModal && !cropModal.hidden) closeCrop();
    });

    setPetNameFont(petNamePoster?.dataset.renaoPetNameFont || 'amsterdam');
    if (!isNoteMode) setProvider(providerInput.value || 'Spotify');
    syncSelectedTrackNotice();
    syncText();
    syncCode();
    syncPhotoTransform();

    const editState = getCartEditState();
    if (editState) {
      applyCartEditState(editState);
    } else {
      const editKey = getCartEditKeyFromUrl();
      if (editKey) fetchCartEditState(editKey).then(applyCartEditState);
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-renao-music-personalizer]').forEach(initMusicPersonalizer);
  });
})();
